/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * LookupFieldConfig — settings for a field whose choices come from a table.
 *
 * Everything chosen here is stored on the field and read by the server when the
 * picker asks for its options. The browser never names a table at run time; it
 * names this field, and the server looks the rest up. So this panel is the only
 * place the table is ever decided.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHelper } from '../../../hooks/usePages';
import { useFormBuilderContext } from './formBuilderContext';

const MODES = [
  { value: 'dropdown', label: 'Dropdown',     hint: 'A plain list. Best under ~50 rows.' },
  { value: 'search',   label: 'Search box',   hint: 'Type to filter. Best for hundreds.' },
  { value: 'popup',    label: 'Search popup', hint: 'Opens a dialog. Best for thousands.' },
];

const rowStyle  = { marginBottom: 8 };
const lblStyle  = { fontSize: 10, color: '#374151', fontWeight: 600, marginBottom: 4 };
const ctrlStyle = {
  width: '100%', padding: '4px 8px', fontSize: 11,
  border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', background: '#fff',
};

export default function LookupFieldConfig({ field, onUpdate }) {
  const { appId, fields } = useFormBuilderContext();
  const config = field.lookup || {};

  const set = patch => onUpdate({ lookup: { ...config, ...patch } });

  const { data: tablesData, isLoading } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200`,
        { method: 'GET', signal },
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = tablesData?.tables || tablesData?.results || [];
  const table = tables.find(t => t.name === config.table);

  const { data: details } = useQuery({
    queryKey: ['tableColumns', appId, table?.id],
    queryFn: async ({ signal }) => {
      // POST, not GET: this endpoint takes its arguments in the body and
      // returns 405 for anything else.
      const resp = await apiHelper.apiCall(
        '/arcee/application/table/details/',
        {
          method: 'POST',
          body: JSON.stringify({ application_id: appId, table_id: table.id }),
          signal,
        },
      );
      if (!resp.ok) return { fields: [] };
      return resp.json();
    },
    enabled: !!table?.id,
    staleTime: 60_000,
  });

  const columns = details?.fields || [];
  const otherFields = (fields || []).filter(f => f.id !== field.id && f.type === 'lookup');

  return (
    <div style={{
      border: '1px solid #e9d5ff', background: '#faf5ff',
      borderRadius: 6, padding: '8px 10px', marginBottom: 8,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, color: '#7e22ce',
        letterSpacing: '0.08em', marginBottom: 8,
      }}>
        WHERE THE CHOICES COME FROM
      </div>

      <div style={rowStyle}>
        <div style={lblStyle}>Table</div>
        <select style={ctrlStyle} value={config.table || ''}
          onChange={e => set({ table: e.target.value, label_column: '', search_columns: [] })}>
          <option value="">{isLoading ? 'Loading…' : '— Choose a table —'}</option>
          {tables.map(t => (
            <option key={t.id} value={t.name}>{t.display_name || t.name}</option>
          ))}
        </select>
      </div>

      <div style={rowStyle}>
        <div style={lblStyle}>How it looks</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {MODES.map(m => (
            <button key={m.value} type="button" title={m.hint}
              onClick={() => set({ mode: m.value })}
              style={{
                flex: 1, fontSize: 10, padding: '3px 4px', borderRadius: 4,
                border: '1px solid',
                borderColor: (config.mode || 'dropdown') === m.value ? '#7e22ce' : '#e2e8f0',
                background: (config.mode || 'dropdown') === m.value ? '#f3e8ff' : '#fff',
                color: (config.mode || 'dropdown') === m.value ? '#7e22ce' : '#64748b',
                fontWeight: (config.mode || 'dropdown') === m.value ? 700 : 400,
                cursor: 'pointer',
              }}>
              {m.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 3 }}>
          {(MODES.find(m => m.value === (config.mode || 'dropdown')) || MODES[0]).hint}
        </div>
      </div>

      {table && (
        <>
          <div style={rowStyle}>
            <div style={lblStyle}>Show which column</div>
            <select style={ctrlStyle} value={config.label_column || ''}
              onChange={e => set({ label_column: e.target.value })}>
              <option value="">
                The table's own name column{table.record_identifier ? ` (${table.record_identifier})` : ''}
              </option>
              {columns.map(c => (
                <option key={c.name} value={c.name}>{c.display_name || c.name}</option>
              ))}
            </select>
          </div>

          {otherFields.length > 0 && (
            <div style={rowStyle}>
              <div style={lblStyle}>Narrow it by another choice</div>
              <select style={ctrlStyle}
                value={(config.depends_on || {}).field || ''}
                onChange={e => {
                  if (!e.target.value) set({ depends_on: undefined });
                  else set({ depends_on: { ...(config.depends_on || {}), field: e.target.value } });
                }}>
                <option value="">— Show everything —</option>
                {otherFields.map(f => (
                  <option key={f.id} value={f.id}>After “{f.label || f.id}” is chosen</option>
                ))}
              </select>
              {(config.depends_on || {}).field && (
                <select style={{ ...ctrlStyle, marginTop: 4 }}
                  value={(config.depends_on || {}).column || ''}
                  onChange={e => set({
                    depends_on: { ...(config.depends_on || {}), column: e.target.value },
                  })}>
                  <option value="">— Match on which column? —</option>
                  {columns.map(c => (
                    <option key={c.name} value={c.name}>{c.display_name || c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </>
      )}

      {!config.table && (
        <div style={{ fontSize: 9, color: '#a16207', background: '#fefce8',
          border: '1px solid #fde68a', borderRadius: 4, padding: '5px 7px' }}>
          <i className="bi bi-exclamation-triangle me-1" />
          Choose a table, or this field will have nothing to offer.
        </div>
      )}
    </div>
  );
}
